import { PencilIcon, TrashIcon } from './medicine-fields';
import {
  formatIntakeDay,
  formatMedicineSubtitle,
  formatScheduleSummary,
  getIntakeDays,
} from './order-medicine';
import type { OrderMedicine } from './order-medicine';

/** Кружок с изображением препарата; если картинки нет — серая заглушка. */
const MedicineAvatar = ({ image }: { image?: string }) => (
  <div className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#f9f9fc]">
    {image ? (
      <img src={image} alt="" className="size-7 object-contain" />
    ) : (
      <div className="h-3.5 w-7 rounded bg-[#e6e8ed]" />
    )}
  </div>
);

type Props = {
  medicine: OrderMedicine;
  onEdit: () => void;
  onDelete: () => void;
};

/**
 * Добавленный препарат: схема приёма одной строкой и лента пакетиков по дням
 * курса. Лента листается горизонтально, полосу прокрутки не показываем.
 */
export const MedicineCard = ({ medicine, onEdit, onDelete }: Props) => {
  const subtitle = formatMedicineSubtitle(medicine.medicine);
  const { dates, rest } = getIntakeDays(medicine);

  return (
    <div className="flex w-full flex-col gap-3 rounded-2xl border border-[#deecf5] p-4">
      <div className="flex w-full items-center gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="truncate text-base font-medium leading-5 text-[#151515]">
            {medicine.medicine.name}
          </span>
          {subtitle && (
            <span className="truncate text-sm font-normal leading-4 text-[#151515] opacity-50">
              {subtitle}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onEdit}
          className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[#f7f8f9] text-[#151515]"
          aria-label={`Изменить ${medicine.medicine.name}`}
        >
          <PencilIcon className="size-4" />
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[#f7f8f9] text-[#151515]"
          aria-label={`Удалить ${medicine.medicine.name}`}
        >
          <TrashIcon className="size-4" />
        </button>
      </div>

      <p className="w-full text-sm font-normal leading-4 text-[#151515]">
        {formatScheduleSummary(medicine)}
      </p>

      {dates.length > 0 && (
        <div className="-mx-1 flex w-[calc(100%+0.5rem)] gap-3 overflow-x-auto px-1 scrollbar-hide">
          {dates.map((date) => {
            const { day, month } = formatIntakeDay(date);
            return (
              <div
                key={date.toISOString()}
                className="flex shrink-0 basis-[10.5rem] flex-col gap-2 rounded-xl border border-[#deecf5] md:basis-[calc((100%-1.5rem)/3)]"
              >
                <div className="flex items-center gap-0.5 px-3 pt-3">
                  <span className="text-sm font-medium leading-[18px] text-[#0788f1]">
                    {day}
                  </span>
                  <span className="truncate text-sm font-normal leading-[18px] text-black">
                    {month}
                  </span>
                </div>
                <div className="flex items-center gap-2 px-3 pb-3">
                  <MedicineAvatar image={medicine.medicine.picture?.image} />
                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <span className="truncate text-sm font-medium leading-5 text-[#151515]">
                      {medicine.medicine.name}
                    </span>
                    {subtitle && (
                      <span className="truncate text-xs font-normal leading-[14px] text-[#151515] opacity-50">
                        {subtitle}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {rest > 0 && (
            <div className="flex shrink-0 basis-[10.5rem] items-center justify-center rounded-xl border border-dashed border-[#deecf5] p-3 text-sm font-normal leading-4 text-[#151515] opacity-50 md:basis-[calc((100%-1.5rem)/3)]">
              и ещё {rest}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
